
import { useState, useEffect, useRef } from "react"
import Plot from "react-plotly.js"
import Textarea from "./ui/textarea"
import React from "react"
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card"
import { Download, Edit, Copy, Share, History, X, ChevronRight, Trash2, Loader2 } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip"
const Button = React.forwardRef(({ className, variant = "default", size = "default", ...props }, ref) => {
  const baseStyles =
    "inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background"

  const variants = {
    default: "bg-primary text-primary-foreground hover:bg-primary/90",
    destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
    outline: "border border-input hover:bg-accent hover:text-accent-foreground",
    secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
    ghost: "hover:bg-accent hover:text-accent-foreground",
    link: "underline-offset-4 hover:underline text-primary",
  }

  const sizes = {
    default: "h-10 py-2 px-4",
    sm: "h-9 px-3 rounded-md",
    lg: "h-11 px-8 rounded-md",
    icon: "h-10 w-10",
  }

  return <button className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`} ref={ref} {...props} />
})

Button.displayName = "Button"
const currentUrl = window.location.href
const ipAddress = currentUrl.split(":")[1].split("/")[2]

// IndexedDB utility functions
const DB_NAME = 'PlotGeneratorDB';
const DB_VERSION = 1;
const HISTORY_STORE = 'plotHistory';

// Open the database
const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = (event) => {
      console.error('Error opening IndexedDB:', event.target.error);
      reject(event.target.error);
    };
    
    request.onsuccess = (event) => {
      resolve(event.target.result);
    };
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      
      // Create object store if it doesn't exist
      if (!db.objectStoreNames.contains(HISTORY_STORE)) {
        const store = db.createObjectStore(HISTORY_STORE, { keyPath: 'id' });
        // Create indexes for faster queries
        store.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
  });
};

// Save history to IndexedDB
const saveHistoryToDB = async (history) => {
  try {
    const db = await openDB();
    const transaction = db.transaction([HISTORY_STORE], 'readwrite');
    const store = transaction.objectStore(HISTORY_STORE);
    
    // Clear existing data
    store.clear();
    
    // Add all history items
    for (const item of history) {
      store.add(item);
    }
    
    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = (event) => reject(event.target.error);
    });
  } catch (error) {
    console.error('Error saving history to IndexedDB:', error);
    // Fallback to localStorage if IndexedDB fails
    try {
      localStorage.setItem("plotGeneratorHistory", JSON.stringify(history));
    } catch (e) {
      console.error('Error saving to localStorage (fallback):', e);
    }
  }
};

// Get history from IndexedDB
const getHistoryFromDB = async () => {
  try {
    const db = await openDB();
    const transaction = db.transaction([HISTORY_STORE], 'readonly');
    const store = transaction.objectStore(HISTORY_STORE);
    const index = store.index('timestamp');
    
    // Get all records sorted by timestamp (newest first)
    const request = index.openCursor(null, 'prev');
    
    return new Promise((resolve, reject) => {
      const items = [];
      
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          items.push(cursor.value);
          cursor.continue();
        } else {
          resolve(items);
        }
      };
      
      request.onerror = (event) => reject(event.target.error);
    });
  } catch (error) {
    console.error('Error getting history from IndexedDB:', error);
    // Fallback to localStorage if IndexedDB fails
    try {
      const savedHistory = localStorage.getItem("plotGeneratorHistory");
      return savedHistory ? JSON.parse(savedHistory) : [];
    } catch (e) {
      console.error('Error loading from localStorage (fallback):', e);
      return [];
    }
  }
};

// Clear all data from IndexedDB
const clearAllDataFromDB = async () => {
  try {
    const db = await openDB();
    const transaction = db.transaction([HISTORY_STORE], 'readwrite');
    const store = transaction.objectStore(HISTORY_STORE);
    
    store.clear();
    
    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = (event) => reject(event.target.error);
    });
  } catch (error) {
    console.error('Error clearing data from IndexedDB:', error);
    // Fallback to localStorage if IndexedDB fails
    try {
      localStorage.removeItem("plotGeneratorHistory");
    } catch (e) {
      console.error('Error removing from localStorage (fallback):', e);
    }
  }
};

// Add a single item to history in IndexedDB
const addItemToHistoryDB = async (item) => {
  try {
    const db = await openDB();
    const transaction = db.transaction([HISTORY_STORE], 'readwrite');
    const store = transaction.objectStore(HISTORY_STORE);
    
    store.add(item);
    
    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = (event) => reject(event.target.error);
    });
  } catch (error) {
    console.error('Error adding item to IndexedDB:', error);
    // No fallback needed as this is handled by the main saveHistoryToDB function
  }
};

// Remove a single item from history in IndexedDB
const removeItemFromHistoryDB = async (id) => {
  try {
    const db = await openDB();
    const transaction = db.transaction([HISTORY_STORE], 'readwrite');
    const store = transaction.objectStore(HISTORY_STORE);
    
    store.delete(id);
    
    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = (event) => reject(event.target.error);
    });
  } catch (error) {
    console.error('Error removing item from IndexedDB:', error);
    // No fallback needed as this is handled by the main saveHistoryToDB function
  }
};

function PlotGenerator() {
  const [mathFunction, setMathFunction] = useState("")
  const [plotCode, setPlotCode] = useState("1")
  const [plotUrl, setPlotUrl] = useState("")
  const [ButtonDisabled, setButtonDisabled] = useState(false)
  const [d3ButtonDisabled, setD3ButtonDisabled] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const [dataD3Plot, setDataD3Plot] = useState("")
  const [dataSketchPlot, setDataSketchplot] = useState("")
  const [sketchButtonDisabled, setSketchButtonDisabled] = useState(false)
  const [isMathContent, setIsMathContent] = useState(true)
  const [loadingUploadImage, setLoadingUploadImage] = useState(false)
  //edit image tool
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editingImage, setEditingImage] = useState(null)
  const [drawingColor, setDrawingColor] = useState("#FF0000") // Red default
  const [brushSize, setBrushSize] = useState(5)
  const [isDrawing, setIsDrawing] = useState(false)
  const [tool, setTool] = useState("pen") // pen, eraser
  const canvasRef = useRef(null)
  const contextRef = useRef(null)
  // New state for history and sidebar
  const [history, setHistory] = useState([])
  const [sidebarOpen, setSidebarOpen] = useState(false)


  const textareaRef = useRef(null)
  const API_URL = "https://math-image-transcriber-steveszren.replit.app/api/convert"
  useEffect(() => {
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  }, []);
  // Load history from IndexedDB on component mount
  useEffect(() => {
    const loadHistory = async () => {
      try {
        const historyItems = await getHistoryFromDB();
        if (historyItems && historyItems.length > 0) {
          setHistory(historyItems);
        } else {
          // Try to migrate from localStorage if IndexedDB is empty
          const savedHistory = localStorage.getItem("plotGeneratorHistory");
          if (savedHistory) {
            try {
              const parsedHistory = JSON.parse(savedHistory);
              setHistory(parsedHistory);
              // Save migrated data to IndexedDB
              await saveHistoryToDB(parsedHistory);
              // Optionally clear localStorage after migration
              // localStorage.removeItem("plotGeneratorHistory");
            } catch (e) {
              console.error("Error parsing history from localStorage:", e);
              localStorage.removeItem("plotGeneratorHistory");
            }
          }
        }
      } catch (error) {
        console.error("Error loading history:", error);
      }
    };
    
    loadHistory();
  }, []);

  // Save history to IndexedDB whenever it changes
  useEffect(() => {
    if (history.length > 0) {
      saveHistoryToDB(history).catch(error => {
        console.error("Error saving history to IndexedDB:", error);
      });
    }
  }, [history]);

  // Fix the addToHistory function to directly use the current data rather than relying on state
  const addToHistory = (imageUrlData = null, plotData = null) => {
    if (!mathFunction) return;

    // Create a new history item with the current data
    const newItem = {
      id: Date.now().toString(),
      inputText: mathFunction,
      timestamp: Date.now(),
      isMathContent: isMathContent,
    };

    // Use the passed data or fall back to state
    if (imageUrlData) {
      newItem.imageUrl = imageUrlData;
    } else if (plotUrl) {
      newItem.imageUrl = plotUrl;
    }

    if (plotData) {
      newItem.plotData = plotData;
    } else if (dataD3Plot) {
      newItem.plotData = dataD3Plot;
    }

    // Use a callback to ensure we're working with the latest state
    setHistory((prevHistory) => {
      const updatedHistory = [newItem, ...prevHistory.slice(0, 19)];
      
      // We don't need to save to localStorage here anymore as the useEffect will handle it
      return updatedHistory;
    });
    
    // Optionally, add the item directly to IndexedDB
    // This can be more efficient than saving the entire history array each time
    addItemToHistoryDB(newItem).catch(error => {
      console.error("Error adding item directly to IndexedDB:", error);
    });
  };

  // Function to load item from history
  const loadFromHistory = (item) => {
    setMathFunction(item.inputText)
    setPlotUrl(item.imageUrl || "")
    setDataD3Plot(item.plotData || "")
    setIsMathContent(item.isMathContent)
    if (item.imageUrl) {
      setPlotCode("dalle")
    }

    // Close sidebar on mobile after selection
    if (window.innerWidth < 768) {
      setSidebarOpen(false)
    }
  }

  // Function to remove item from history
  const removeFromHistory = (id, e) => {
    e.stopPropagation(); // Prevent triggering the parent click event
    
    // Remove from state
    setHistory((prev) => prev.filter((item) => item.id !== id));
    
    // Also remove directly from IndexedDB
    removeItemFromHistoryDB(id).catch(error => {
      console.error("Error removing item directly from IndexedDB:", error);
    });
  };

  // Function to clear all history
  const clearHistory = () => {
    if (window.confirm("Are you sure you want to clear all history?")) {
      setHistory([]);
      clearAllDataFromDB().catch(error => {
        console.error("Error clearing data from IndexedDB:", error);
      });
    }
  };
  const insertTextAtCursor = (text) => {
    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;

    const newValue = mathFunction.substring(0, start) + text + mathFunction.substring(end);
    setMathFunction(newValue);

    setTimeout(() => {
        textarea.focus();
        textarea.selectionStart = textarea.selectionEnd = start + text.length;
    }, 0);
};

const handlePaste = (event) => {
    const items = event.clipboardData?.items;
    if (!items) return;

    for (let item of items) {
        if (item.type.indexOf("image") === 0) {
            event.preventDefault();

            const file = item.getAsFile();
            const formData = new FormData();
            formData.append("image", file);

            setLoadingUploadImage(true);
            setErrorMessage(null);

            fetch(API_URL, {
                method: "POST",
                body: formData,
            })
                .then((response) => response.json())
                .then((data) => {
                    if (data.success && data.result) {
                        insertTextAtCursor(data.result);
                    } else {
                        setErrorMessage(data.error || "Failed to convert image");
                    }
                })
                .catch(() => setErrorMessage("Error uploading image"))
                .finally(() => setLoadingUploadImage(false));

            break;
        }
    }
};

  // Function to download the current image
  const downloadImage = () => {
    if (plotUrl) {
      // For regular images
      const link = document.createElement("a")
      link.href = plotUrl
      link.download = `plot-${Date.now()}.png`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } else if (dataD3Plot) {
      // For Plotly plots
      const plotElement = document.querySelector(".js-plotly-plot")
      if (plotElement && window.Plotly) {
        window.Plotly.downloadImage(plotElement, {
          format: "png",
          filename: `plot-${Date.now()}`,
          height: 800,
          width: 1200,
          scale: 2,
        })
      }
    }
  }
  // Add this function to handle opening the edit modal
  const openEditModal = () => {
    setEditingImage(plotUrl)
    setIsEditModalOpen(true)

        // Initialize canvas in the next render cycle
        setTimeout(() => {
            const canvas = canvasRef.current
            if (canvas) {
                const context = canvas.getContext("2d")
                context.lineCap = "round"
                context.strokeStyle = drawingColor
                context.lineWidth = brushSize
                contextRef.current = context

                // Load the image onto the canvas
                const img = new Image()
                img.crossOrigin = "anonymous"
                img.onload = () => {
                    // Set canvas dimensions to match the image
                    canvas.width = img.width
                    canvas.height = img.height
                    context.drawImage(img, 0, 0)

                    context.lineCap = "round"
                    context.strokeStyle = tool === "eraser" ? "#FFFFFF" : drawingColor
                    context.lineWidth = brushSize
                }
                img.onerror = (err) => {
                    console.error("Error loading image:", err)
                    // Provide a fallback canvas size if image fails to load
                    canvas.width = 800
                    canvas.height = 600
                    context.fillStyle = "#ffffff"
                    context.fillRect(0, 0, canvas.width, canvas.height)

                    // Draw error message
                    context.fillStyle = "#ff0000"
                    context.font = "20px Arial"
                    context.textAlign = "center"
                    context.fillText("Error loading image", canvas.width / 2, canvas.height / 2)
                }

                // Handle both base64 and URL sources
                const imageToUse = plotUrl // use it directly
                if (imageToUse.startsWith("data:")) {
                    img.src = imageToUse
                } else {
                    convertImageUrlToBase64(imageToUse)
                        .then((base64Url) => {
                            img.src = base64Url
                        })
                        .catch((err) => {
                            console.error("Failed to convert image:", err)
                            img.src = imageToUse // fallback
                        })
                }
            }
        }, 100)
    }

    // Add these functions to handle drawing
    const startDrawing = (e) => {
        const canvas = canvasRef.current
        const context = contextRef.current

        if (!canvas || !context) return

        const rect = canvas.getBoundingClientRect()
        let clientX, clientY

        if (e.type === "touchstart") {
            e.preventDefault()
            const touch = e.touches[0]
            clientX = touch.clientX
            clientY = touch.clientY
        } else {
            clientX = e.clientX
            clientY = e.clientY
        }

        // Calculate position relative to the canvas
        const offsetX = clientX - rect.left
        const offsetY = clientY - rect.top

        // Scale the coordinates if the canvas display size differs from its internal size
        const scaleX = canvas.width / rect.width
        const scaleY = canvas.height / rect.height

        context.beginPath()
        context.moveTo(offsetX * scaleX, offsetY * scaleY)
        setIsDrawing(true)
    }

    const draw = (e) => {
        if (!isDrawing || !contextRef.current || !canvasRef.current) return

        const canvas = canvasRef.current
        const rect = canvas.getBoundingClientRect()
        let clientX, clientY

        if (e.type === "touchmove") {
            e.preventDefault()
            const touch = e.touches[0]
            clientX = touch.clientX
            clientY = touch.clientY
        } else {
            clientX = e.clientX
            clientY = e.clientY
        }

        // Calculate position relative to the canvas
        const offsetX = clientX - rect.left
        const offsetY = clientY - rect.top

        // Scale the coordinates if the canvas display size differs from its internal size
        const scaleX = canvas.width / rect.width
        const scaleY = canvas.height / rect.height

        contextRef.current.lineTo(offsetX * scaleX, offsetY * scaleY)
        contextRef.current.stroke()
    }

    const finishDrawing = () => {
        setIsDrawing(false)
        if (contextRef.current) {
            contextRef.current.closePath()
        }
    }


    // Add this function to handle saving the edited image
    const saveEditedImage = () => {
        if (canvasRef.current) {
            const editedImageUrl = canvasRef.current.toDataURL("image/png")
            setPlotUrl(editedImageUrl)
            setIsEditModalOpen(false)

            // Update history with the edited image
            addToHistory(editedImageUrl, null)
        }
    }

    // Add this function to handle changing the drawing color
    const handleColorChange = (e) => {
        const newColor = e.target.value
        setDrawingColor(newColor)
        if (contextRef.current) {
            contextRef.current.strokeStyle = tool === "eraser" ? "#FFFFFF" : newColor
        }
    }

    // Add this function to handle changing the brush size
    const handleBrushSizeChange = (e) => {
        const newSize = Number.parseInt(e.target.value)
        setBrushSize(newSize)
        if (contextRef.current) {
            contextRef.current.lineWidth = newSize
        }
    }

    // Add this function to handle changing the tool
    const handleToolChange = (newTool) => {
        setTool(newTool)
        if (contextRef.current) {
            contextRef.current.strokeStyle = newTool === "eraser" ? "#FFFFFF" : drawingColor
        }
    }

    // Add this function to handle resizing the image
    const handleResize = (percentage) => {
        const canvas = canvasRef.current
        const context = canvas.getContext("2d")

        // Create a temporary canvas to hold the current image
        const tempCanvas = document.createElement("canvas")
        const tempContext = tempCanvas.getContext("2d")
        tempCanvas.width = canvas.width
        tempCanvas.height = canvas.height
        tempContext.drawImage(canvas, 0, 0)

        // Calculate new dimensions
        const newWidth = Math.floor(canvas.width * (percentage / 100))
        const newHeight = Math.floor(canvas.height * (percentage / 100))

        // Resize the main canvas
        canvas.width = newWidth
        canvas.height = newHeight

        // Draw the image back at the new size
        context.drawImage(tempCanvas, 0, 0, tempCanvas.width, tempCanvas.height, 0, 0, newWidth, newHeight)

        // Restore context properties
        context.lineCap = "round"
        context.strokeStyle = tool === "eraser" ? "#FFFFFF" : drawingColor
        context.lineWidth = brushSize
    }

  // Add a function to convert remote image URLs to local base64 data
  // Add this function after the classifyContentWithAI function
  const convertImageUrlToBase64 = async (imageUrl) => {
    try {
      // Encode the URL so it can be safely passed as a query param
      const encodedUrl = encodeURIComponent(imageUrl)

      // Request the image via your backend proxy
      const response = await fetch(`/api/image-proxy?url=${encodedUrl}`)

      if (!response.ok) {
        throw new Error(`Proxy fetch failed: ${response.status}`)
      }

      const blob = await response.blob()

      return await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result) // data:image/png;base64,...
        reader.onerror = reject
        reader.readAsDataURL(blob)
      })
    } catch (error) {
      console.error("Error converting image to base64 via proxy:", error)
      return null
    }
  }

  // Function to compress an image before storing it
  const compressImage = (imageUrl, maxWidth = 800, quality = 0.8) => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.crossOrigin = "anonymous"

      img.onload = () => {
        // Create a canvas element
        const canvas = document.createElement("canvas")

        // Calculate new dimensions while maintaining aspect ratio
        let width = img.width
        let height = img.height

        if (width > maxWidth) {
          height = Math.round(height * (maxWidth / width))
          width = maxWidth
        }

        // Set canvas dimensions
        canvas.width = width
        canvas.height = height

        // Draw image on canvas with new dimensions
        const ctx = canvas.getContext("2d")
        ctx.drawImage(img, 0, 0, width, height)

        // Convert to compressed base64 data URL
        const compressedBase64 = canvas.toDataURL("image/jpeg", quality)

        resolve(compressedBase64)
      }

      img.onerror = (error) => {
        reject(error)
      }

      img.src = imageUrl
    })
  }

  // Function to copy image to clipboard
  const copyToClipboard = async () => {
    try {
      if (plotUrl) {
        const response = await fetch(plotUrl)
        const blob = await response.blob()
        await navigator.clipboard.write([
          new ClipboardItem({
            [blob.type]: blob,
          }),
        ])
        alert("Image copied to clipboard!")
      } else {
        alert("No image available to copy")
      }
    } catch (err) {
      console.error("Failed to copy image: ", err)
      alert("Failed to copy image to clipboard")
    }
  }

    // Function to share the image
    const shareImage = async () => {
        if (!plotUrl && !dataD3Plot) {
            alert("No image available to share")
            return
        }

        if (navigator.share) {
            try {
                const shareData = {
                    title: "My Generated Plot",
                    text: `Plot of ${mathFunction}`,
                }

                if (plotUrl) {
                    // Try to share with the image file
                    try {
                        const response = await fetch(plotUrl)
                        const blob = await response.blob()
                        const file = new File([blob], "plot.png", { type: "image/png" })
                        shareData.files = [file]
                    } catch (e) {
                        console.error("Couldn't prepare image for sharing:", e)
                    }
                }

                await navigator.share(shareData)
            } catch (err) {
                console.error("Error sharing:", err)
            }
        } else {
            alert("Web Share API not supported in your browser")
        }
    }

    const checkIfMathContent = (input) => {
        if (!input || input.trim() === "") return false

        // 1. Check for common math symbols and patterns
        const mathSymbolsRegex = /[+\-*/^=<>$$$$[\]{}∫∬∭∮∯∰∇∆∂√∛∜∝∞≠≈≤≥±∓∑∏]/
        if (mathSymbolsRegex.test(input)) return true

        // 2. Check for numbers with decimal points or scientific notation
        const numberRegex = /\b\d+\.\d+\b|\b\d+e[+-]?\d+\b/
        if (numberRegex.test(input)) return true

        // 3. Check for common math functions
        const mathFunctionsRegex =
            /\b(sin|cos|tan|cot|sec|csc|arcsin|arccos|arctan|sinh|cosh|tanh|log|ln|exp|sqrt|cbrt|abs|floor|ceil|round|min|max|gcd|lcm|mod|factorial|prime|fibonacci)\b/i
        if (mathFunctionsRegex.test(input)) return true

    // 4. Check for common math terms
    const mathTermsRegex =
      /\b(function|equation|formula|graph|plot|curve|integral|derivative|calculus|algebra|polynomial|quadratic|cubic|exponential|logarithm|trigonometric|hyperbolic|matrix|vector|tensor|eigenvalue|determinant|theorem|proof|lemma|corollary|axiom|postulate|identity|inequality|sequence|series|limit|infinity|converge|diverge|asymptote|discontinuity|continuous|differentiable|integrable|linear|nonlinear|differential|partial|ordinary|homogeneous|heterogeneous|symmetric|asymmetric|orthogonal|parallel|perpendicular|tangent|normal|binomial|multinomial|coefficient|factorial|permutation|combination|probability|statistics|distribution|random|variable|expected|variance|standard deviation|correlation|regression|hypothesis|confidence|significance|p-value|null hypothesis|alternative hypothesis|test statistic|critical value|rejection region|type i error|type ii error|power|sample|population|parameter|estimator|bias|efficiency|consistency|sufficiency|completeness|robustness|outlier|residual|error|precision|accuracy|mean|median|mode|quartile|percentile|range|interquartile range|variance|standard deviation|skewness|kurtosis|moment|central moment|standardized moment|correlation|covariance|regression|least squares|maximum likelihood|bayesian|prior|posterior|likelihood|conjugate|markov|chain|monte carlo|simulation|bootstrap|jackknife|cross-validation|validation|training|test|overfitting|underfitting|regularization|ridge|lasso|elastic net|principal component|discriminant|cluster|classification|regression|supervised|unsupervised|reinforcement|neural network|deep learning|machine learning|artificial intelligence|algorithm|complexity|big o|little o|omega|theta|recursion|iteration|induction|base case|inductive step|strong induction|well-ordering principle|pigeonhole principle|inclusion-exclusion principle|dirichlet principle|extremal principle|invariant principle|monovariants|potential function|greedy algorithm|dynamic programming|divide and conquer|backtracking|branch and bound|heuristic|approximation|randomized algorithm|probabilistic algorithm|las vegas algorithm|monte carlo algorithm|online algorithm|offline algorithm|streaming algorithm|parallel algorithm|distributed algorithm|quantum algorithm|cryptography|encryption|decryption|key|public key|private key|symmetric key|asymmetric key|rsa|diffie-hellman|elliptic curve|hash function|digital signature|authentication|authorization|integrity|confidentiality|non-repudiation|availability|security|privacy|anonymity|pseudonymity|identity|access control|firewall|intrusion detection|intrusion prevention|malware|virus|worm|trojan|spyware|adware|ransomware|phishing|spoofing|denial of service|distributed denial of service|buffer overflow|sql injection|cross-site scripting|cross-site request forgery|man-in-the-middle|replay attack|timing attack|side-channel attack|social engineering|physical security|biometrics|password|passphrase|pin|token|smart card|certificate|certification authority|public key infrastructure|trust|trustworthy|trusted|trustless|blockchain|distributed ledger|consensus|proof of work|proof of stake|proof of authority|proof of burn|proof of capacity|proof of elapsed time|proof of importance|proof of space|proof of space-time|proof of retrievability|proof of replication|proof of custody|proof of existence|proof of ownership|proof of possession|proof of knowledge|zero-knowledge proof|interactive proof|non-interactive proof|succinct proof|probabilistically checkable proof|multi-party computation|secure multi-party computation|homomorphic encryption|fully homomorphic encryption|somewhat homomorphic encryption|leveled homomorphic encryption|lattice-based cryptography|post-quantum cryptography|quantum-resistant cryptography|quantum key distribution|quantum teleportation|quantum entanglement|quantum superposition|quantum interference|quantum measurement|quantum decoherence|quantum error correction|quantum fault tolerance|quantum supremacy|quantum advantage|quantum speedup|quantum parallelism|quantum algorithm|quantum circuit|quantum gate|quantum bit|qubit|quantum register|quantum memory|quantum communication|quantum internet|quantum network|quantum repeater|quantum sensor|quantum metrology|quantum imaging|quantum lithography|quantum computing|quantum computer|quantum processor|quantum annealing|quantum simulation|quantum chemistry|quantum biology|quantum physics|quantum mechanics|quantum field theory|quantum electrodynamics|quantum chromodynamics|quantum gravity|quantum cosmology)\b/i
    if (mathTermsRegex.test(input)) return true

    // 5. Check for variable assignments or equations
    const equationRegex =
      /\b[a-zA-Z]\s*[+\-*/^]\s*[a-zA-Z0-9]|\b[a-zA-Z]\s*=\s*[a-zA-Z0-9]|\b[a-zA-Z]$$[a-zA-Z]$$|\b[a-zA-Z]_[a-zA-Z0-9]|\b[a-zA-Z]\s*\{\s*[a-zA-Z0-9]|\b[a-zA-Z]\s*\[\s*[a-zA-Z0-9]/
    if (equationRegex.test(input)) return true

    // 6. Check for LaTeX-like notation
    const latexRegex = /\\\w+\{|\\\w+\[|\\\w+\(|\$\$|\$|\\\[|\\\]|\\begin\{|\\end\{/
    if (latexRegex.test(input)) return true

    // 7. Check for common math phrases
    const mathPhrasesRegex =
      /\b(plot of|graph of|function of|derivative of|integral of|solve for|calculate|compute|evaluate|simplify|expand|factor|distribute|find the|determine the|prove that|show that|demonstrate that|verify that|establish that|given that|assume that|suppose that|let|consider|define|denote|represent|symbolize|express|formulate|state|assert|claim|conjecture|hypothesize|postulate|axiomatize|theorem|lemma|corollary|proposition|identity|equation|inequality|formula|relation|function|mapping|transformation|operator|algorithm|procedure|method|technique|approach|strategy|tactic|heuristic|rule|law|principle|property|characteristic|attribute|feature|aspect|dimension|parameter|variable|constant|coefficient|factor|term|expression|polynomial|rational|irrational|real|complex|imaginary|integer|natural|whole|prime|composite|even|odd|positive|negative|zero|infinity|finite|infinite|bounded|unbounded|convergent|divergent|monotonic|increasing|decreasing|non-increasing|non-decreasing|strictly increasing|strictly decreasing|weakly increasing|weakly decreasing|convex|concave|differentiable|integrable|continuous|discontinuous|piecewise|smooth|rough|linear|nonlinear|affine|quadratic|cubic|quartic|quintic|sextic|septic|octic|nonic|decic|polynomial|exponential|logarithmic|trigonometric|hyperbolic|periodic|aperiodic|symmetric|asymmetric|even|odd|injective|surjective|bijective|invertible|inverse|composition|identity|kernel|image|range|domain|codomain|preimage|subspace|quotient space|direct sum|tensor product|inner product|outer product|cross product|dot product|scalar product|vector product|triple product|wedge product|exterior product|symmetric product|antisymmetric product|commutator|anticommutator|lie bracket|poisson bracket|moyal bracket|dirac bracket|star product|convolution|correlation|fourier transform|laplace transform|z transform|hilbert transform|mellin transform|hankel transform|abel transform|radon transform|wavelet transform|short-time fourier transform|discrete fourier transform|fast fourier transform|discrete cosine transform|discrete sine transform|discrete wavelet transform|continuous wavelet transform|gabor transform|wigner transform|wigner-ville transform|cohen class|time-frequency|time-scale|multiresolution|filter bank|perfect reconstruction|orthogonal filter|biorthogonal filter|finite impulse response|infinite impulse response|causal|anticausal|minimum phase|maximum phase|mixed phase|all-pass|low-pass|high-pass|band-pass|band-stop|notch|comb|resonator|oscillator|modulator|demodulator|mixer|heterodyne|superheterodyne|homodyne|phase-locked loop|voltage-controlled oscillator|numerically controlled oscillator|direct digital synthesis|frequency synthesis|phase synthesis|amplitude modulation|frequency modulation|phase modulation|pulse-amplitude modulation|pulse-width modulation|pulse-position modulation|pulse-code modulation|delta modulation|sigma-delta modulation|adaptive delta modulation|differential pulse-code modulation|linear predictive coding|code-excited linear prediction|vector quantization|scalar quantization|entropy coding|huffman coding|arithmetic coding|lempel-ziv coding|run-length encoding|dictionary coding|transform coding|predictive coding|differential coding|subband coding|wavelet coding|fractal coding|model-based coding|parametric coding|sinusoidal coding|harmonic coding|source coding|channel coding|error correction|error detection|forward error correction|automatic repeat request|hybrid automatic repeat request|convolutional code|block code|turbo code|low-density parity-check code|reed-solomon code|bch code|hamming code|golay code|repetition code|cyclic code|systematic code|nonsystematic code|linear code|nonlinear code|perfect code|mds code|rate-compatible code|punctured code|shortened code|extended code|interleaved code|concatenated code|product code|serial concatenation|parallel concatenation|iterative decoding|belief propagation|message passing|sum-product algorithm|min-sum algorithm|viterbi algorithm|bcjr algorithm|map decoding|maximum likelihood decoding|maximum a posteriori decoding|soft-decision decoding|hard-decision decoding|chase decoding|generalized minimum distance decoding|ordered statistics decoding|reliability-based decoding|list decoding|algebraic decoding|syndrome decoding|majority-logic decoding|iterative decoding|turbo decoding|ldpc decoding|belief propagation decoding|message passing decoding|sum-product decoding|min-sum decoding|bit-flipping decoding|weighted bit-flipping decoding|gallager a decoding|gallager b decoding|quantized decoding|analog decoding|mixed-signal decoding|asynchronous decoding|synchronous decoding|parallel decoding|serial decoding|pipelined decoding|folded decoding|unfolded decoding|early termination|stopping criterion|convergence|divergence|fixed point|cycle|trapping set|absorbing set|pseudocodeword|graph|bipartite graph|factor graph|tanner graph|check node|variable node|degree|regular|irregular|protograph|quasi-cyclic|spatially coupled|terminated|tailbiting|recursive|nonrecursive|systematic|nonsystematic|catastrophic|noncatastrophic|memory|constraint length|free distance|minimum distance|hamming distance|euclidean distance|manhattan distance|chebyshev distance|mahalanobis distance|kullback-leibler distance|hellinger distance|total variation distance|wasserstein distance|earth mover distance|jensen-shannon distance|bhattacharyya distance|chernoff distance|divergence|relative entropy|mutual information|conditional entropy|joint entropy|differential entropy|shannon entropy|renyi entropy|tsallis entropy|von neumann entropy|quantum entropy|entanglement entropy|topological entropy|metric entropy|kolmogorov-sinai entropy|approximate entropy|sample entropy|permutation entropy|transfer entropy|granger causality|directed information|capacity|channel capacity|capacity-achieving|capacity-approaching|shannon limit|shannon bound|shannon theory|information theory|coding theory|communication theory|signal processing|digital signal processing|analog signal processing|mixed-signal processing|discrete-time|continuous-time|sampled-data|sampling|quantization|reconstruction|interpolation|decimation|upsampling|downsampling|multirate|polyphase)\b/i
    if (mathPhrasesRegex.test(input)) return true

    // 8. Check for common math units and constants
    const mathConstantsRegex =
      /\b(pi|euler|e|i|infinity|infty|aleph|beth|gimel|daleth|alpha|beta|gamma|delta|epsilon|zeta|eta|theta|iota|kappa|lambda|mu|nu|xi|omicron|pi|rho|sigma|tau|upsilon|phi|chi|psi|omega|degree|radian|steradian|hertz|newton|pascal|joule|watt|coulomb|volt|farad|ohm|siemens|weber|tesla|henry|lumen|lux|becquerel|gray|sievert|katal|meter|gram|second|ampere|kelvin|mole|candela|planck|boltzmann|avogadro|gas|stefan|speed of light|gravitational|permittivity|permeability|fine structure|rydberg|bohr|hartree|atomic|electron|proton|neutron|muon|tau|quark|gluon|photon|w boson|z boson|higgs|graviton|neutrino|lepton|hadron|baryon|meson|fermion|gauge|standard model|grand unified|supersymmetry|string theory|m-theory|f-theory|p-brane|d-brane|ns5-brane|m2-brane|m5-brane|black hole|event horizon|singularity|hawking radiation|bekenstein-hawking entropy|black hole thermodynamics|black hole information paradox|firewall|complementarity|stretched horizon|fuzzball|gravastar|dark star|planck star|regular black hole|nonsingular black hole|quantum black hole|micro black hole|primordial black hole|supermassive black hole|intermediate-mass black hole|stellar black hole|kerr|schwarzschild|reissner-nordstrom|kerr-newman|extremal|non-extremal|rotating|charged|neutral|static|stationary|axisymmetric|spherically symmetric|asymptotically flat|asymptotically de sitter|asymptotically anti-de sitter|cosmological horizon|apparent horizon|killing horizon|cauchy horizon|isolated horizon|dynamical horizon|trapping horizon|marginally trapped surface|trapped surface|anti-trapped surface|normal surface|white hole|wormhole|einstein-rosen bridge|morris-thorne|traversable|non-traversable|time machine|closed timelike curve|chronology protection|grandfather paradox|bootstrap paradox|predestination paradox|novikov self-consistency principle|many-worlds interpretation|copenhagen interpretation|pilot wave theory|de broglie-bohm|objective collapse|ghirardi-rimini-weber|continuous spontaneous localization|penrose interpretation|transactional interpretation|relational interpretation|ensemble interpretation|modal interpretation|consistent histories|quantum bayesianism|qbism|quantum darwinism|existential interpretation|participatory anthropic principle|wheeler|feynman|bohr|einstein|heisenberg|schrodinger|dirac|pauli|born|jordan|von neumann|wigner|bell|aspect|clauser|horne|shimony|holt|freedman|bohm|everett|dewitt|zurek|zeh|joos|kiefer|giulini|kupsch|stamatescu|blanchard|omnès|griffiths|gell-mann|hartle|penrose|rovelli|smolin|ashtekar|thiemann|baez|crane|barrett|quantum|classical|relativistic|non-relativistic|special relativity|general relativity|lorentz|poincare|galilean|newton|lagrangian|hamiltonian|action|principle of least action|euler-lagrange|hamilton-jacobi|canonical|symplectic|poisson|liouville|ergodic|mixing|chaotic|integrable|non-integrable|kac|kolmogorov|arnold|moser|kam|poincare-birkhoff|twist|area-preserving|volume-preserving|measure-preserving|recurrence|transitive|minimal|uniquely ergodic|weakly mixing|strongly mixing|bernoulli|markov|anosov|axiom a|smale horseshoe|solenoid|baker's map|cat map|standard map|chirikov|henon|logistic|tent|doubling|circle|rotation|irrational|rational|resonance|quasi-periodic|periodic|fixed point|invariant|attractor|repeller|saddle|basin|stable|unstable|hyperbolic|non-hyperbolic|lyapunov|exponent|dimension|capacity|information|correlation|hausdorff|box-counting|pointwise|information|topological|metric|fractal|multifractal|self-similar|self-affine|iterated function system|lindenmayer|mandelbrot|julia|fatou|cantor|sierpinski|koch|peano|hilbert|space-filling|dragon|levy|brownian|fractional brownian|gaussian|white noise|pink noise|brown noise|black noise|blue noise|violet noise|grey noise|green noise|orange noise|red noise|1\/f noise|1\/f\^2 noise|shot noise|thermal noise|johnson-nyquist noise|flicker noise|burst noise|avalanche noise|quantum noise|vacuum fluctuation|zero-point energy|casimir effect|lamb shift|spontaneous emission|stimulated emission|absorption|scattering|reflection|refraction|diffraction|interference|superposition|entanglement|decoherence|measurement|collapse|wave function|state vector|density matrix|pure state|mixed state|superposition|coherent|incoherent|separable|entangled|bell state|ghz state|w state|cluster state|graph state|stabilizer state|code state|logical state|physical state|computational basis|bell basis|pauli basis|gell-mann basis|bloch sphere|poincare sphere|hilbert space|fock space|liouville space|banach space|sobolev space|schwartz space|tempered distribution|distribution|generalized function|dirac delta|heaviside step|rectangular|triangular|sawtooth|square|pulse|sinc|gaussian|lorentzian|cauchy|laplacian|poisson|binomial|multinomial|hypergeometric|negative binomial|geometric|exponential|gamma|beta|dirichlet|chi-squared|student's t|fisher's f|snedecor's f|wishart|inverse wishart|multivariate normal|multivariate t|wishart|inverse wishart|dirichlet|multinomial|categorical|bernoulli|binomial|poisson|exponential|gamma|beta|uniform|normal|gaussian|log-normal|pareto|weibull|gumbel|frechet|generalized extreme value|generalized pareto|von mises|circular uniform|wrapped cauchy|wrapped normal|von mises-fisher|kent|bingham|watson|fisher-bingham|matrix fisher|matrix bingham|wishart|inverse wishart|matrix normal|matrix t|matrix beta|matrix gamma|dirichlet|beta|gamma|chi-squared|inverse chi-squared|scaled inverse chi-squared|inverse gamma|pareto|half-normal|half-cauchy|half-t|folded normal|truncated normal|skew normal|generalized normal|exponential power|asymmetric laplace|logistic|log-logistic|gumbel|frechet|weibull|generalized extreme value|generalized pareto|burr|fisk|dagum|singh-maddala|generalized beta|kumaraswamy|power|power-normal|power-lognormal|power-exponential|power-pareto|power-weibull|power-logistic|power-log-logistic|power-gamma|power-inverse-gamma|power-chi-squared|power-inverse-chi-squared|power-scaled-inverse-chi-squared|power-f|power-t|power-beta|power-dirichlet|power-wishart|power-inverse-wishart|power-matrix-normal|power-matrix-t|power-matrix-beta|power-matrix-gamma|power-matrix-fisher|power-matrix-bingham|power-von-mises|power-von-mises-fisher|power-kent|power-bingham|power-watson|power-fisher-bingham|power-circular-uniform|power-wrapped-cauchy|power-wrapped-normal|power-generalized-extreme-value|power-generalized-pareto|power-gumbel|power-frechet|power-weibull|power-burr|power-fisk|power-dagum|power-singh-maddala|power-generalized-beta|power-kumaraswamy)\b/i
    if (mathConstantsRegex.test(input)) return true

    // 9. Check for common math abbreviations
    const mathAbbreviationsRegex =
      /\b(lim|sup|inf|max|min|arg|det|tr|diag|rank|ker|im|dom|cod|id|Re|Im|grad|div|curl|lapl|hess|jac|diff|int|sum|prod|lim|sup|inf|gcd|lcm|lcm|mod|rem|frac|binom|choose|perm|comb|fact|sqrt|cbrt|log|ln|exp|sin|cos|tan|cot|sec|csc|arcsin|arccos|arctan|arccot|arcsec|arccsc|sinh|cosh|tanh|coth|sech|csch|arcsinh|arccosh|arctanh|arccoth|arcsech|arccsch)\b/i
    if (mathAbbreviationsRegex.test(input)) return true

    // 10. Check for common math notation patterns
    const mathNotationRegex =
      /f$$x$$|y=|f:|R->|\\mathbb{R}|\\mathbb{Z}|\\mathbb{N}|\\mathbb{Q}|\\mathbb{C}|\\frac{|\\sqrt{|\\int_|\\sum_|\\prod_|\\lim_|\\sup_|\\inf_|\\max_|\\min_|\\arg_|\\det_|\\tr_|\\diag_|\\rank_|\\ker_|\\im_|\\dom_|\\cod_|\\id_|\\Re_|\\Im_|\\grad_|\\div_|\\curl_|\\lapl_|\\hess_|\\jac_|\\diff_|\\int_|\\sum_|\\prod_|\\lim_|\\sup_|\\inf_|\\gcd_|\\lcm_|\\lcm_|\\mod_|\\rem_|\\frac_|\\binom_|\\choose_|\\perm_|\\comb_|\\fact_|\\sqrt_|\\cbrt_|\\log_|\\ln_|\\exp_|\\sin_|\\cos_|\\tan_|\\cot_|\\sec_|\\csc_|\\arcsin_|\\arccos_|\\arctan_|\\arccot_|\\arcsec_|\\arccsc_|\\sinh_|\\cosh_|\\tanh_|\\coth_|\\sech_|\\csch_|\\arcsinh_|\\arccosh_|\\arctanh_|\\arccoth_|\\arcsech_|\\arccsch_/
    if (mathNotationRegex.test(input)) return true

    // If none of the above patterns match, it's likely not math content
    return false
  }

    const classifyContentWithAI = async (input) => {
        try {
            const res = await fetch("/api/classify-content", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ text: input }),
            })

            if (!res.ok) {
                // Fall back to heuristic approach if API fails
                return checkIfMathContent(input)
            }

            const data = await res.json()
            return data.isMathContent
        } catch (error) {
            console.error("Content classification error:", error)
            // Fall back to heuristic approach if API fails
            return checkIfMathContent(input)
        }
    }

    // Fix the handleDalleGeneration function to properly format the base64 data and pass it directly to addToHistory
    const handleDalleGeneration = async (customPrompt) => {
        setErrorMessage(null)
        setButtonDisabled(true)

        try {
            const res = await fetch("/api/generate-dalle-image", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
          prompt: customPrompt, // fallback to mathFunction
                    ipAddress: ipAddress,
                }),
            })

            if (!res.ok) {
                const errorData = await res.json()
                throw new Error(errorData.message || "An error occurred while generating the image.")
            }

            const data = await res.json()
            // Ensure the image URL is properly formatted and convert to base64 if needed
            let imageUrl = data.imageUrl

      // If it's not a base64 URL, convert it to ensure persistence
      if (!imageUrl.startsWith("data:")) {
        try {
          let base64Image

          // First convert to base64
          base64Image = await convertImageUrlToBase64(imageUrl)

          if (base64Image) {
            // Then compress the base64 image
            const compressedImage = await compressImage(base64Image, 800, 0.85)
            imageUrl = compressedImage
          } else {
            console.error("Failed to convert image to base64, using original URL")
            // If conversion fails, we'll still try to use the direct URL
          }
        } catch (error) {
          console.error("Error converting/compressing image:", error)
          // If conversion fails, we'll still try to use the direct URL
        }
      }

                setPlotUrl(imageUrl)
                setPlotCode("dalle") // Mark as DALL-E generated

            // Add to history with the actual data, not relying on state update
            addToHistory(imageUrl, null)
        } catch (error) {
            console.error("DALL-E API Error:", error)
            setErrorMessage(error.message || "An unexpected error occurred. Please try again.")
        } finally {
            setButtonDisabled(false)
        }
    }

    // Fix the handleSubmit function to properly format the base64 data and pass it directly to addToHistory
    const handleSubmit = async (e) => {
        e.preventDefault()
        setErrorMessage(null)
        setButtonDisabled(true)

        try {
            // First check if the content is math-related using the manual check
            let mathContent = checkIfMathContent(mathFunction)

            // If manual check doesn't detect math, use AI classification as fallback
            if (!mathContent) {
                mathContent = await classifyContentWithAI(mathFunction)
            }

            setIsMathContent(mathContent)

            if (mathContent) {
                // Original math plot generation code
                try {
                    const res = await fetch("/api/generate-plot", {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                            mathFunction: mathFunction,
                            ipAddress: ipAddress,
                        }),
                    })
                    if (!res.ok) {
                        const errorData = await res.json()
                        throw new Error(errorData.message || "An error occurred while generating the plot.")
                    }
                    const data = await res.json()

                    // Ensure the image URL is properly formatted if it's base64 data
                    const imageUrl = data.plotBase64.startsWith("data:")
                        ? data.plotBase64
                        : `data:image/png;base64,${data.plotBase64}`

                    setPlotCode(data.plotCode)
                    setPlotUrl(imageUrl)

                    // Add to history with the actual data, not relying on state update
                    addToHistory(imageUrl, null)
                } catch (error) {
                    console.error("API Error:", error)
                    setErrorMessage("An unexpected error occurred. Please try again.")

                    if (error.response) {
                        console.error("Error Response:", error.response.data)
                        if (error.response.status === 500) {
                            setErrorMessage(error.response.data.message || "An unexpected error occurred on the server.")
                        } else {
                            setErrorMessage(error.response.data.message || "An error occurred.")
                        }
                    } else if (error.message) {
                        if (error.message === "Network Error") {
                            setErrorMessage("Network error: Unable to reach the server. Please try again later.")
                        } else if (error.code === "ECONNABORTED") {
                            setErrorMessage("Request Timeout: The request took too long to complete. Please try again later.")
                        } else {
                            setErrorMessage(`Unexpected error: I'm not smart enough for this task. Try something easier`)
                        }
                    } else {
                        setErrorMessage("An unexpected error occurred. Please try again.")
                    }
                }
            } else {
                // Non-math content - call DALL-E API
                await handleDalleGeneration(mathFunction)
            }
        } catch (error) {
            console.error("Error in content classification:", error)
            setErrorMessage("Error classifying content. Defaulting to DALL-E image generation.")
            await handleDalleGeneration(mathFunction)
        } finally {
            setButtonDisabled(false)
        }
    }

    // Fix the handleSketchSubmit function to properly format the base64 data and pass it directly to addToHistory
    const handleSketchSubmit = async (e) => {
        e.preventDefault()
        setErrorMessage(null)
        setSketchButtonDisabled(true)
        // Send the user's math function request to the backend
        try {
            // First check if the content is math-related using the manual check
            let mathContent = checkIfMathContent(mathFunction)

            // If manual check doesn't detect math, use AI classification as fallback
            if (!mathContent) {
                mathContent = await classifyContentWithAI(mathFunction)
            }

            setIsMathContent(mathContent)

            if (mathContent) {
                try {
                    const res = await fetch("/api/generate-plot-sketch", {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                            mathFunction: mathFunction,
                            ipAddress: ipAddress,
                        }),
                    })
                    if (!res.ok) {
                        // If the response status is not OK (e.g., 500), throw an error
                        const errorData = await res.json() // Parse the error message from the response
                        throw new Error(errorData.message || "An error occurred while generating the plot.")
                    }
                    const data = await res.json()

                    // Ensure the image URL is properly formatted if it's base64 data
                    const imageUrl = data.plotBase64.startsWith("data:")
                        ? data.plotBase64
                        : `data:image/png;base64,${data.plotBase64}`

                    setPlotCode(data.plotCode)
                    setPlotUrl(imageUrl)

                    // Add to history with the actual data, not relying on state update
                    addToHistory(imageUrl, null)
                } catch (error) {
                    console.error("API Error:", error)
                    setErrorMessage("An unexpected error occurred. Please try again.")

                    // Check if the error has a response (from the backend)
                    if (error.response) {
                        console.error("Error Response:", error.response.data)

                        // Handle server errors (e.g., 500)
                        if (error.response.status === 500) {
                            setErrorMessage(error.response.data.message || "An unexpected error occurred on the server.")
                        } else {
                            setErrorMessage(error.response.data.message || "An error occurred.")
                        }
                    }
                    // Handle network or other client-side errors (e.g., timeout, connection issues)
                    else if (error.message) {
                        if (error.message === "Network Error") {
                            setErrorMessage("Network error: Unable to reach the server. Please try again later.")
                        } else if (error.code === "ECONNABORTED") {
                            setErrorMessage("Request Timeout: The request took too long to complete. Please try again later.")
                        } else {
                            setErrorMessage(`Unexpected error: I'm not smart enough for this task. Try something easier`)
                        }
                    }
                    // Handle cases where error object is not properly structured (fallback)
                    else {
                        setErrorMessage("An unexpected error occurred. Please try again.")
                    }
                }
            } else {
                // Non-math content - call DALL-E API
                await handleDalleGeneration("black and white no color very raw hand sketch of " + mathFunction)
            }
        } catch (error) {
            console.error("Error in content classification:", error)
            setErrorMessage("Error classifying content. Defaulting to DALL-E image generation.")
            await handleDalleGeneration(mathFunction)
        } finally {
            setSketchButtonDisabled(false)
        }
    }

    // Fix the handleD3Submit function to pass the plot data directly to addToHistory
    const handleD3Submit = async (e) => {
        e.preventDefault()
        setErrorMessage(null)
        setD3ButtonDisabled(true)
        try {
            // Send request to backend with function name
            const res = await fetch("/api/generate-d3plot", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    mathFunction: mathFunction,
                    ipAddress: ipAddress,
                }),
            })
            if (!res.ok) {
                // If the response status is not OK (e.g., 500), throw an error
                const errorData = await res.json() // Parse the error message from the response
                throw new Error(errorData.message || "An error occurred while generating the plot.")
            }
            const result = await res.json()
            const parsedPlotData = JSON.parse(result.plotData)
            setDataD3Plot(parsedPlotData) // Receive dataset and store it in state

            // Add to history with the actual data, not relying on state update
            addToHistory(null, parsedPlotData)
        } catch (error) {
            console.error("API Error:", error)
            setErrorMessage("An unexpected error occurred. Please try again.")

            // Check if the error has a response (from the backend)
            if (error.response) {
                console.error("Error Response:", error.response.data)

                // Handle server errors (e.g., 500)
                if (error.response.status === 500) {
                    setErrorMessage(error.response.data.message || "An unexpected error occurred on the server.")
                } else {
                    setErrorMessage(error.response.data.message || "An error occurred.")
                }
            }
            // Handle network or other client-side errors (e.g., timeout, connection issues)
            else if (error.message) {
                if (error.message === "Network Error") {
                    setErrorMessage("Network error: Unable to reach the server. Please try again later.")
                } else if (error.code === "ECONNABORTED") {
                    setErrorMessage("Request Timeout: The request took too long to complete. Please try again later.")
                } else {
                    setErrorMessage(`Unexpected error: ${error.message}`)
                }
            }
            // Handle cases where error object is not properly structured (fallback)
            else {
                setErrorMessage("An unexpected error occurred. Please try again.")
            }
        }
        setD3ButtonDisabled(false)
    }

    // Format date for history items
    const formatDate = (timestamp) => {
        const date = new Date(timestamp)
        return date.toLocaleString()
    }

    // Truncate text for display in history
    const truncateText = (text, maxLength = 30) => {
        if (!text) return ""
        return text.length > maxLength ? text.substring(0, maxLength) + "..." : text
    }

    return (
        <div className="flex flex-col md:flex-row items-start p-4 md:p-6 space-y-6 md:space-y-0 md:space-x-0 bg-gradient-to-br from-indigo-50 via-purple-50 to-blue-50 dark:from-gray-900 dark:via-indigo-950/30 dark:to-gray-900 min-h-screen relative">
            {/* History Sidebar */}
            <div
                className={`fixed inset-y-0 right-0 z-30 w-72 bg-white/90 dark:bg-gray-900/90 backdrop-blur-md shadow-xl border-l border-gray-200 dark:border-gray-800 transition-transform duration-300 ${sidebarOpen ? "translate-x-0" : "translate-x-full"
                    }`}
            >
                <div className="flex flex-col h-full">
                    <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
                        <h2 className="text-lg font-semibold flex items-center">
                            <History className="w-5 h-5 mr-2" />
                            History
                        </h2>
                        <div className="flex items-center space-x-2">
                            {history.length > 0 && (
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" onClick={clearHistory}>
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>Clear all history</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            )}
                            {/* Fix the X button in the sidebar to properly close it */}
                            {/* Replace the existing Button component in the sidebar header with this: */}
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 rounded-full"
                                onClick={() => {
                                    setSidebarOpen(false)
                                }}
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                    <div className="flex-1 overflow-y-auto p-3 space-y-2 custom-scrollbar">
                        {history.length === 0 ? (
                            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                <History className="h-12 w-12 mx-auto mb-3 opacity-50" />
                                <p>No history yet</p>
                                <p className="text-sm mt-1">Generated plots will appear here</p>
                            </div>
                        ) : (
                            history.map((item) => (
                                <div
                                    key={item.id}
                                    className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700 transition-all cursor-pointer overflow-hidden"
                                    onClick={() => loadFromHistory(item)}
                                >
                                    <div className="p-2">
                                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{formatDate(item.timestamp)}</div>
                                        <div className="text-sm font-medium mb-2 line-clamp-1">{truncateText(item.inputText, 40)}</div>
                                        <div className="relative h-24 bg-gray-100 dark:bg-gray-900 rounded overflow-hidden">
                                            {item.imageUrl ? (
                                                <img
                                                    src={item.imageUrl || "/placeholder.svg"}
                                                    alt={item.inputText}
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : item.plotData ? (
                                                <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-900">
                                                    <div className="text-xs text-gray-500 dark:text-gray-400">Interactive Plot</div>
                                                </div>
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <div className="text-xs text-gray-500 dark:text-gray-400">No preview</div>
                                                </div>
                                            )}
                                            <button
                                                className="absolute top-1 right-1 bg-gray-800/70 hover:bg-red-600/90 text-white rounded-full p-1 opacity-0 hover:opacity-100 focus:opacity-100 transition-opacity"
                                                onClick={(e) => removeFromHistory(item.id, e)}
                                            >
                                                <X className="h-3 w-3" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>

      {/* Sidebar Toggle Button (Mobile) */}
      <button
        className={`absolute right-0 top-1/5 -translate-y-1/2 bg-white dark:bg-gray-800 shadow-md rounded-l-lg p-2 z-20 md:hidden ${
          sidebarOpen ? "hidden" : "block"
        }`}
        onClick={() => setSidebarOpen(true)}
      >
        <i className="fas fa-archive text-blue-600 text-lg mr-2"></i>
      </button>

            {/* Desktop Sidebar Toggle Button */}
            <button
                className={`fixed right-4 top-24 bg-white dark:bg-gray-100 shadow-md rounded-full p-2 z-20 hidden md:block`}
                onClick={() => setSidebarOpen(!sidebarOpen)}
            >
                {sidebarOpen ? <ChevronRight className="h-5 w-5" /> : <History className="h-5 w-5" />}
            </button>

            {/* Main Content */}
            <div className={`flex flex-col w-full transition-all duration-300 ${sidebarOpen ? "md:mr-72" : "md:mr-72"}`}>
                <div className="flex flex-col md:flex-row items-start space-y-6 md:space-y-0 md:space-x-6 w-full">
                    {/* Left side - Input area */}
                    <div className="flex flex-col w-full md:w-1/2 lg:w-2/5 space-y-6 relative">
                        {/* Decorative elements */}
                        <div className="absolute -top-10 -left-10 w-64 h-64 bg-blue-300/20 dark:bg-blue-500/10 rounded-full filter blur-3xl"></div>
                        <div className="absolute top-1/2 -right-10 w-64 h-64 bg-purple-300/20 dark:bg-purple-500/10 rounded-full filter blur-3xl"></div>

            {/* Input Card */}
            <Card className="glass-morphism shadow-xl border border-gray-200/50 dark:border-gray-700/50 rounded-2xl overflow-hidden hover:shadow-blue-500/10 transition-all duration-300 animate-fade-in">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                    Create Your Visualization for Math
                  </CardTitle>
                  <div className="text-sm font-medium">
                    {mathFunction && (
                      <span
                        className={`flex felx-col px-3 py-1 rounded-full ${
                          checkIfMathContent(mathFunction)
                            ? "bg-gradient-to-r from-blue-500/20 to-blue-600/20 text-blue-700 dark:text-blue-900 border border-blue-300/30"
                            : "bg-gradient-to-r from-purple-500/20 to-pink-600/20 text-purple-700 dark:text-purple-900 border border-purple-300/30"
                        } transition-all duration-300 shadow-sm`}
                      >
                        <span className="flex felx-col items-center">
                          {checkIfMathContent(mathFunction) ? (
                            <>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="inline h-4 w-4 mr-1.5 align-middle"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M2 6h4v12H2z"></path>
                                <path d="M22 6h-4v12h4z"></path>
                                <path d="M10 9h4"></path>
                                <path d="M10 15h4"></path>
                                <path d="M14 12h-4"></path>
                              </svg>
                              Math Content
                            </>
                          ) : (
                            <>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4 mr-1.5 flex-shrink-0"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M12 19l7-7 3 3-7 7-3-3z"></path>
                                <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"></path>
                                <path d="M2 2l7.586 7.586"></path>
                                <circle cx="11" cy="11" r="2"></circle>
                              </svg>
                              Creative Content
                            </>
                          )}
                        </span>
                      </span>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Styled Input Box */}
                  <div className="relative">
                    <textarea
                        ref={textareaRef}
                      className="p-5 border-2 border-gray-300/50 dark:border-gray-700/50 rounded-xl w-full bg-gray-900/95 text-white text-lg font-mono shadow-lg ring-offset-2 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-400/50 focus:border-blue-500/50 focus:outline-none transition-all duration-300"
                      placeholder="e.g., 'sin(x) * cos(x)' or 'a futuristic city with flying cars'"
                      value={mathFunction}
                      onChange={(e) => setMathFunction(e.target.value)}
                      rows="4"
                      onPaste={handlePaste}
                      disabled={ButtonDisabled || d3ButtonDisabled || loadingUploadImage}
                    />                                    
                    {loadingUploadImage && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-gray-800/80 rounded-lg">
                            <div className="flex flex-col items-center gap-2">
                                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                                <span className="text-gray-700 dark:text-gray-300 font-medium">Processing image...</span>
                            </div>
                        </div>
                    )}
                    <div className="absolute bottom-3 right-3 text-xs text-gray-500 dark:text-gray-400 opacity-70">
                      Type your expression or concept here
                    </div>
                  </div>

                  {/* Generate Plot Button */}
                  <Button
                    type="submit"
                    className={`w-full text-lg font-semibold py-4 rounded-xl shadow-md transition-all duration-300 ${
                      ButtonDisabled || d3ButtonDisabled
                        ? "bg-gray-900/80 cursor-not-allowed opacity-50"
                        : "bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white transform hover:scale-[1.02] hover:shadow-lg hover:shadow-blue-500/20"
                    }`}
                    onClick={() => {
                      setPlotCode(null)
                      setPlotUrl(null)
                      setDataD3Plot(null)
                      setDataSketchplot(null)
                    }}
                    disabled={ButtonDisabled || d3ButtonDisabled || sketchButtonDisabled}
                  >
                    {ButtonDisabled ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="flex space-x-1">
                          <div
                            className="h-2 w-2 bg-white rounded-full animate-bounce"
                            style={{ animationDelay: "0ms" }}
                          ></div>
                          <div
                            className="h-2 w-2 bg-white rounded-full animate-bounce"
                            style={{ animationDelay: "150ms" }}
                          ></div>
                          <div
                            className="h-2 w-2 bg-white rounded-full animate-bounce"
                            style={{ animationDelay: "300ms" }}
                          ></div>
                        </div>
                        <span>Generating...</span>
                      </div>
                    ) : (
                      <span className="flex items-center justify-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 mr-2"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M12 5v14"></path>
                          <path d="M5 12h14"></path>
                        </svg>
                        Generate Image
                      </span>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Alternative Visualization Options */}
            <Card className="glass-morphism shadow-xl border border-gray-200/50 dark:border-gray-700/50 rounded-2xl overflow-hidden hover:shadow-blue-500/10 transition-all duration-300 animate-fade-in delay-100">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Alternative Visualizations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex space-x-4 justify-center">
                  {/* Interactive Plot Button */}
                  <form onSubmit={handleD3Submit} className="w-1/2">
                    <Button
                      type="submit"
                      className={`w-full text-base font-semibold py-3 px-4 rounded-xl shadow-md transition-all duration-300 ${
                        ButtonDisabled || d3ButtonDisabled
                          ? "bg-gray-900/80 cursor-not-allowed opacity-50"
                          : "bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white transform hover:scale-[1.02] hover:shadow-lg hover:shadow-emerald-500/20"
                      }`}
                      onClick={() => {
                        setPlotCode(null)
                        setPlotUrl(null)
                        setDataD3Plot(null)
                        setDataSketchplot(null)
                      }}
                      disabled={ButtonDisabled || d3ButtonDisabled || sketchButtonDisabled}
                    >
                      {d3ButtonDisabled ? (
                        <div className="flex items-center justify-center space-x-2">
                          <div className="flex space-x-1">
                            <div
                              className="h-2 w-2 bg-white rounded-full animate-bounce"
                              style={{ animationDelay: "0ms" }}
                            ></div>
                            <div
                              className="h-2 w-2 bg-white rounded-full animate-bounce"
                              style={{ animationDelay: "150ms" }}
                            ></div>
                            <div
                              className="h-2 w-2 bg-white rounded-full animate-bounce"
                              style={{ animationDelay: "300ms" }}
                            ></div>
                          </div>
                          <span>Processing...</span>
                        </div>
                      ) : (
                        <span className="flex items-center justify-center">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 mr-1"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <circle cx="12" cy="12" r="10"></circle>
                            <path d="M12 16v-4"></path>
                            <path d="M12 8h.01"></path>
                          </svg>
                          Interactive Plot
                        </span>
                      )}
                    </Button>
                  </form>

                  {/* Plot From A Human Button */}
                  <form onSubmit={handleSketchSubmit} className="w-1/2">
                    <Button
                      type="submit"
                      className={`w-full text-base font-semibold py-3 px-4 rounded-xl shadow-md transition-all duration-300 ${
                        ButtonDisabled || d3ButtonDisabled || sketchButtonDisabled
                          ? "bg-gray-900/80 cursor-not-allowed opacity-50"
                          : "bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white transform hover:scale-[1.02] hover:shadow-lg hover:shadow-purple-500/20"
                      }`}
                      onClick={() => {
                        setPlotCode(null)
                        setPlotUrl(null)
                        setDataSketchplot(null)
                        setDataD3Plot(null)
                      }}
                      disabled={ButtonDisabled || d3ButtonDisabled || sketchButtonDisabled}
                    >
                      {sketchButtonDisabled ? (
                        <div className="flex items-center justify-center space-x-2">
                          <div className="flex space-x-1">
                            <div
                              className="h-2 w-2 bg-white rounded-full animate-bounce"
                              style={{ animationDelay: "0ms" }}
                            ></div>
                            <div
                              className="h-2 w-2 bg-white rounded-full animate-bounce"
                              style={{ animationDelay: "150ms" }}
                            ></div>
                            <div
                              className="h-2 w-2 bg-white rounded-full animate-bounce"
                              style={{ animationDelay: "300ms" }}
                            ></div>
                          </div>
                          <span>Processing...</span>
                        </div>
                      ) : (
                        <span className="flex items-center justify-center">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 mr-1"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M12 19l7-7 3 3-7 7-3-3z"></path>
                            <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"></path>
                            <path d="M2 2l7.586 7.586"></path>
                            <circle cx="11" cy="11" r="2"></circle>
                          </svg>
                          Hand-Drawn Style
                        </span>
                      )}
                    </Button>
                  </form>
                </div>
              </CardContent>
            </Card>

            {/* Function Shortcuts */}
            <div className="relative">
              <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-3 flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mr-1"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                  <path d="M2 17l10 5 10-5"></path>
                  <path d="M2 12l10 5 10-5"></path>
                </svg>
                Quick Function Templates
              </h3>
              <div className="flex flex-wrap gap-2 justify-center mt-2 max-h-[180px] overflow-y-auto pr-1 custom-scrollbar">
                {[
                  "Quadratic Function",
                  "Cubic Function",
                  "Sine Wave",
                  "Cosine Wave",
                  "Tangent Curve",
                  "Exponential Growth",
                  "Logarithm Function",
                  "Natural Logarithm",
                  "Absolute Value",
                  "Square Root",
                  "Cube Root",
                  "Reciprocal Function",
                  "Sigmoid Function",
                  "Gaussian Function",
                  "Logistic Growth",
                  "Hyperbolic Sine",
                  "Hyperbolic Cosine",
                  "Hyperbolic Tangent",
                  "Step Function",
                  "Greatest Integer Function",
                ].map((func, index) => (
                  <button
                    key={func}
                    onClick={() => {
                      const functionMappings = {
                        "Quadratic Function": "x^2",
                        "Cubic Function": "x^3",
                        "Sine Wave": "sin(x)",
                        "Cosine Wave": "cos(x)",
                        "Tangent Curve": "tan(x)",
                        "Exponential Growth": "e^x",
                        "Logarithm Function": "log(x)",
                        "Natural Logarithm": "ln(x)",
                        "Absolute Value": "abs(x)",
                        "Square Root": "sqrt(x)",
                        "Cube Root": "cbrt(x)",
                        "Reciprocal Function": "1/x",
                        "Sigmoid Function": "1 / (1 + e^-x)",
                        "Gaussian Function": "e^(-x^2)",
                        "Logistic Growth": "1 / (1 + e^(-x))",
                        "Hyperbolic Sine": "sinh(x)",
                        "Hyperbolic Cosine": "cosh(x)",
                        "Hyperbolic Tangent": "tanh(x)",
                        "Step Function": "H(x) (Heaviside Step)",
                        "Greatest Integer Function": "floor function",
                      }
                      setMathFunction(functionMappings[func])
                    }}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full shadow-sm transition-all duration-300 transform hover:scale-105 hover:shadow-md
                            ${
                              index % 3 === 0
                                ? "bg-gradient-to-r from-blue-500/90 to-blue-600/90 text-white hover:from-blue-600 hover:to-blue-700"
                                : index % 3 === 1
                                  ? "bg-gradient-to-r from-purple-500/90 to-purple-600/90 text-white hover:from-purple-600 hover:to-purple-700"
                                  : "bg-gradient-to-r from-indigo-500/90 to-indigo-600/90 text-white hover:from-indigo-600 hover:to-indigo-700"
                            }`}
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    {func}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right side (Plot Image) */}
          <div className="flex justify-center items-center h-[60vh] md:h-[80vh] w-full md:w-1/2 lg:w-3/5 mt-6 md:mt-0 relative">
            <div className="absolute -top-10 -right-10 w-64 h-64 bg-indigo-300/20 dark:bg-indigo-500/10 rounded-full filter blur-3xl"></div>
            <div className="absolute bottom-10 left-10 w-64 h-64 bg-blue-300/20 dark:bg-blue-500/10 rounded-full filter blur-3xl"></div>

            <div className="w-full max-w-10xl h-full glass-morphism rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 flex justify-center items-center relative animate-fade-in delay-200">
              {/* Show Loading or Placeholder Text */}
              {!plotUrl &&
                !dataD3Plot &&
                !errorMessage &&
                !ButtonDisabled &&
                !d3ButtonDisabled &&
                !sketchButtonDisabled && (
                  <div className="text-center p-8 max-w-md">
                    <div className="mb-6 opacity-80">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-16 w-16 mx-auto text-gray-400 dark:text-gray-600"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="3" y1="9" x2="21" y2="9"></line>
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      Your visualization will appear here
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400">
                      Enter a mathematical expression or creative concept to generate a visualization
                    </p>
                  </div>
                )}

              {/* Show Plot Image with Action Buttons */}
              {plotUrl && (
                <div className="w-full h-full p-4 flex items-center justify-center relative group">
                  <img
                    src={plotUrl || "/placeholder.svg"}
                    alt="Generated visualization"
                    className="object-contain max-w-full max-h-full rounded-lg shadow-md transition-all duration-500 animate-fade-in"
                  />

                  {/* Action buttons overlay */}
                  <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex items-center space-x-2 bg-gray-900/80 backdrop-blur-sm rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-10 w-10 rounded-full bg-gray-800/50 hover:bg-gray-700/70 text-white"
                            onClick={downloadImage}
                          >
                            <Download className="h-5 w-5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Download</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>

                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-10 w-10 rounded-full bg-gray-800/50 hover:bg-gray-700/70 text-white"
                            onClick={openEditModal}
                          >
                            <Edit className="h-5 w-5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Edit</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>

                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-10 w-10 rounded-full bg-gray-800/50 hover:bg-gray-700/70 text-white"
                            onClick={copyToClipboard}
                          >
                            <Copy className="h-5 w-5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Copy to clipboard</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>

                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-10 w-10 rounded-full bg-gray-800/50 hover:bg-gray-700/70 text-white"
                            onClick={shareImage}
                          >
                            <Share className="h-5 w-5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Share</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
              )}

              {/* Show Interactive Plot with Action Buttons */}
              {dataD3Plot && (
                <div className="plot-container rounded-lg p-2 md:p-4 w-full h-full animate-fade-in relative group">
                  <Plot
                    data={dataD3Plot.data}
                    layout={{
                      ...dataD3Plot.layout,
                      autosize: true,
                      paper_bgcolor: "rgba(0,0,0,0)",
                      plot_bgcolor: "rgba(0,0,0,0)",
                      font: {
                        family: "Inter, system-ui, sans-serif",
                        color: "#4b5563",
                      },
                      margin: {
                        l: 50,
                        r: 50,
                        b: 50,
                        t: 50,
                        pad: 4,
                      },
                    }}
                    style={{ width: "100%", height: "100%" }}
                    useResizeHandler={true}
                    config={{
                      responsive: true,
                      displayModeBar: true,
                      scrollZoom: true,
                      staticPlot: false,
                      toImageButtonOptions: {
                        format: "png",
                        filename: "plot",
                        height: 500,
                        width: 700,
                        scale: 2,
                      },
                    }}
                  />

                  {/* Action buttons overlay for interactive plot */}
                  <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex items-center space-x-2 bg-gray-900/80 backdrop-blur-sm rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-10 w-10 rounded-full bg-gray-800/50 hover:bg-gray-700/70 text-white"
                            onClick={downloadImage}
                          >
                            <Download className="h-5 w-5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Download</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>

                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-10 w-10 rounded-full bg-gray-800/50 hover:bg-gray-700/70 text-white"
                            onClick={openEditModal}
                          >
                            <Edit className="h-5 w-5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Edit</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
              )}

              {/* Loading Animation */}
              {!plotCode &&
                !dataD3Plot &&
                !dataSketchPlot &&
                !errorMessage &&
                (ButtonDisabled || d3ButtonDisabled || sketchButtonDisabled) && (
                  <div id="loading-animation" className="absolute flex flex-col items-center justify-center space-y-5">
                    <div className="relative w-24 h-24">
                      <div className="absolute top-0 left-0 w-full h-full border-8 border-gray-200 dark:border-gray-700 rounded-full"></div>
                      <div className="absolute top-0 left-0 w-full h-full border-8 border-transparent border-t-blue-500 rounded-full animate-spin"></div>
                      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-blue-500 dark:text-blue-400">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-8 w-8"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          {isMathContent ? (
                            <>
                              <path d="M2 6h4v12H2z"></path>
                              <path d="M22 6h-4v12h4z"></path>
                              <path d="M10 9h4"></path>
                              <path d="M10 15h4"></path>
                              <path d="M14 12h-4"></path>
                            </>
                          ) : (
                            <>
                              <path d="M12 19l7-7 3 3-7 7-3-3z"></path>
                              <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"></path>
                              <path d="M2 2l7.586 7.586"></path>
                              <circle cx="11" cy="11" r="2"></circle>
                            </>
                          )}
                        </svg>
                      </div>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-medium text-gray-700 dark:text-gray-300">
                        Generating {isMathContent ? "Plot" : "Image"}...
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">This may take a few moments</p>
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>

      {/* Image Edit Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-[95vw] max-w-6xl max-h-[95vh] my-4 flex flex-col overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-500 to-indigo-600 text-white sticky top-0 z-10">
              <h3 className="text-xl font-semibold flex items-center">
                <Edit className="h-5 w-5 mr-2" />
                Image Editor
              </h3>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-full bg-white/20 hover:bg-white/30 text-white"
                onClick={() => setIsEditModalOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex flex-col md:flex-row overflow-auto">
              {/* Tools Panel */}
              <div className="p-5 border-r border-gray-200 dark:border-gray-700 w-full md:w-72 flex flex-col gap-6 bg-gray-50 dark:bg-gray-900 md:min-h-[500px] overflow-y-auto">
                {" "}
                <div>
                  <label className="block text-sm font-medium mb-3 text-gray-700 dark:text-gray-300">
                    Drawing Tools
                  </label>
                  <div className="flex gap-2">
                    <Button
                      className={`flex-1 px-3 py-2 rounded-lg ${
                        tool === "pen"
                          ? "bg-blue-500 text-white shadow-lg shadow-blue-500/30"
                          : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                      } transition-all duration-200`}
                      onClick={() => handleToolChange("pen")}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 mr-2"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M12 19l7-7 3 3-7 7-3-3z"></path>
                        <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"></path>
                      </svg>
                      Pen
                    </Button>
                    <Button
                      className={`flex-1 px-3 py-2 rounded-lg ${
                        tool === "eraser"
                          ? "bg-blue-500 text-white shadow-lg shadow-blue-500/30"
                          : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                      } transition-all duration-200`}
                      onClick={() => handleToolChange("eraser")}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 mr-2"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                        <path d="M15 3h6v6"></path>
                        <path d="M10 14L21 3"></path>
                      </svg>
                      Eraser
                    </Button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-3 text-gray-700 dark:text-gray-300">Color</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={drawingColor}
                      onChange={handleColorChange}
                      className="w-12 h-12 rounded-lg cursor-pointer border-2 border-gray-300 dark:border-gray-600"
                    />
                    <div className="flex-1 grid grid-cols-5 gap-2">
                      {[
                        "#FF0000",
                        "#00FF00",
                        "#0000FF",
                        "#FFFF00",
                        "#FF00FF",
                        "#00FFFF",
                        "#FFA500",
                        "#800080",
                        "#008000",
                        "#000000",
                      ].map((color) => (
                        <div
                          key={color}
                          className={`w-8 h-8 rounded-full cursor-pointer transition-all hover:scale-110 ${drawingColor === color ? "ring-2 ring-offset-2 ring-blue-500" : ""}`}
                          style={{ backgroundColor: color }}
                          onClick={() => {
                            setDrawingColor(color)
                            if (contextRef.current) {
                              contextRef.current.strokeStyle = tool === "eraser" ? "#FFFFFF" : color
                            }
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-3 text-gray-700 dark:text-gray-300">
                    Brush Size: <span className="text-blue-500 font-bold">{brushSize}px</span>
                  </label>
                  <div className="flex items-center gap-3">
                    <span className="text-xs">1px</span>
                    <input
                      type="range"
                      min="1"
                      max="50"
                      value={brushSize}
                      onChange={handleBrushSizeChange}
                      className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700 accent-blue-500"
                    />
                    <span className="text-xs">50px</span>
                  </div>
                  <div className="mt-3 flex justify-between">
                    <Button
                      onClick={() => setBrushSize(5)}
                      className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                    >
                      Small
                    </Button>
                    <Button
                      onClick={() => setBrushSize(15)}
                      className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                    >
                      Medium
                    </Button>
                    <Button
                      onClick={() => setBrushSize(30)}
                      className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                    >
                      Large
                    </Button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-3 text-gray-700 dark:text-gray-300">
                    Resize Image
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    <Button
                      onClick={() => handleResize(50)}
                      className="px-3 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg"
                    >
                      50%
                    </Button>
                    <Button
                      onClick={() => handleResize(75)}
                      className="px-3 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg"
                    >
                      75%
                    </Button>
                    <Button
                      onClick={() => handleResize(100)}
                      className="px-3 py-2 bg-blue-500 text-white hover:bg-blue-600 rounded-lg"
                    >
                      100%
                    </Button>
                    <Button
                      onClick={() => handleResize(125)}
                      className="px-3 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg"
                    >
                      125%
                    </Button>
                    <Button
                      onClick={() => handleResize(150)}
                      className="px-3 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg"
                    >
                      150%
                    </Button>
                    <Button
                      onClick={() => handleResize(200)}
                      className="px-3 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg"
                    >
                      200%
                    </Button>
                  </div>
                </div>
                <div className="mt-auto">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    <p>Tip: Use the pen tool to draw and annotate your image.</p>
                    <p>Use the eraser to remove parts of your drawing.</p>
                  </div>
                </div>
              </div>

                                {/* Canvas Area */}
                                <div className="flex-1 p-4 md:p-6 flex items-center justify-center bg-gray-100 dark:bg-gray-900 relative overflow-auto min-h-[300px]">
                                    <div className="absolute inset-0 opacity-20 pointer-events-none bg-grid-pattern"></div>
                                    <div className="relative shadow-2xl rounded-lg overflow-hidden">
                                        <canvas
                                            ref={canvasRef}
                                            onMouseDown={startDrawing}
                                            onMouseUp={() => setIsDrawing(false)}
                                            onMouseMove={draw}
                                            onMouseLeave={() => setIsDrawing(false)}
                                            onTouchStart={startDrawing}
                                            onTouchEnd={() => setIsDrawing(false)}
                                            onTouchMove={draw}
                                            className="border border-gray-300 dark:border-gray-600 bg-white shadow-md max-w-full max-h-[60vh]"
                                            style={{
                                                cursor:
                                                    tool === "eraser"
                                                        ? "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23000000' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6'%3E%3C/path%3E%3Cpath d='M15 3h6v6'%3E%3C/path%3E%3Cpath d='M10 14L21 3'%3E%3C/path%3E%3C/svg%3E\") 0 24, auto"
                                                        : "crosshair",
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3 bg-gray-50 dark:bg-gray-900">
                            <Button
                                variant="outline"
                                className="px-5 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                                onClick={() => setIsEditModalOpen(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                className="px-5 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white rounded-lg shadow-lg shadow-blue-500/30 transition-all duration-200"
                                onClick={saveEditedImage}
                            >
                                Save Changes
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {errorMessage && (
                <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 p-4 bg-red-100 text-red-700 border border-red-300 rounded-lg shadow-lg animate-fade-in z-50 flex items-center">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 mr-2 text-red-500"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                    <strong>Error:</strong> {errorMessage}
                </div>
            )}

            {/* Add custom scrollbar styles */}
            <style>{`
          .custom-scrollbar::-webkit-scrollbar {
            width: 6px;
            height: 6px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: rgba(0, 0, 0, 0.03);
            border-radius: 8px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: rgba(79, 70, 229, 0.3);
            border-radius: 8px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: rgba(79, 70, 229, 0.5);
          }
          .dark .custom-scrollbar::-webkit-scrollbar-track {
            background: rgba(255, 255, 255, 0.03);
          }
          .dark .custom-scrollbar::-webkit-scrollbar-thumb {
            background: rgba(99, 102, 241, 0.3);
          }
          .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: rgba(99, 102, 241, 0.5);
          }
          
          @keyframes gradient {
            0% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 100% 50%;
            }
            100% {
              background-position: 0% 50%;
            }
          }
          
          .animate-gradient {
            background-size: 200% 200%;
            animation: gradient 8s ease infinite;
          }
          
          @keyframes fade-in {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          .animate-fade-in {
            animation: fade-in 0.5s ease-in-out forwards;
          }
          
          .delay-100 {
            animation-delay: 100ms;
          }
          
          .delay-200 {
            animation-delay: 200ms;
          }
          
          .glass-morphism {
            background: rgba(255, 255, 255, 0.7);
            backdrop-filter: blur(10px);
          }
          
          .dark .glass-morphism {
            background: rgba(17, 24, 39, 0.7);
          }
            
            .bg-grid-pattern {
            background-image: 
              linear-gradient(to right, rgba(128, 128, 128, 0.1) 1px, transparent 1px),
              linear-gradient(to bottom, rgba(128, 128, 128, 0.1) 1px, transparent 1px);
            background-size: 20px 20px;
          }
        `}</style>
        </div>
    )
}

export default PlotGenerator

